import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import Login from "./pages/Login";
import Search from "./pages/Search";
import HospitalDetail from "./pages/HospitalDetail";
import SystemDetail from "./pages/SystemDetail";
import Telemetry from "./pages/Telemetry";
import DataQuality from "./pages/DataQuality";
import AdminAddSystem from "./pages/AdminAddSystem";
import ReportList from "./pages/ReportList";
import ReportNew from "./pages/ReportNew";
import ReportDetail from "./pages/ReportDetail";
import Pricing from "./pages/Pricing";
import Billing from "./pages/Billing";
import AppLayout from "./components/AppLayout";
import { PaywallProvider } from "./lib/paywall";

// Lazy-loaded public share views — code-split so unauthenticated visitors
// don't receive the AuthProvider / Search / DataQuality / Reports chunks.
// See /app/docs/SHAREABLE_SCORECARDS.md → "Security notes".
const SharePage = lazy(() => import("./pages/SharePage"));
const ShareReportPage = lazy(() => import("./pages/ShareReportPage"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <AppLayout>{children}</AppLayout>;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public share routes (NO AuthProvider — pure read-only) */}
        <Route path="/share/report/:token" element={
          <Suspense fallback={<div className="p-8 text-sm text-zinc-500">Loading shared report…</div>}>
            <ShareReportPage />
          </Suspense>
        } />
        <Route path="/share/:token" element={
          <Suspense fallback={<div className="p-8 text-sm text-zinc-500">Loading shared scorecard…</div>}>
            <SharePage />
          </Suspense>
        } />

        {/* Public marketing route — pricing visible without auth */}
        <Route path="/pricing" element={
          <AuthProvider>
            <PaywallProvider><Pricing /></PaywallProvider>
          </AuthProvider>
        } />

        {/* All other routes wrapped in AuthProvider */}
        <Route path="*" element={
          <AuthProvider>
            <PaywallProvider>
              <Routes>
                <Route path="/login" element={<LoginGate />} />
                <Route path="/" element={<Protected><Search /></Protected>} />
                <Route path="/hospitals/:ccn" element={<Protected><HospitalDetail /></Protected>} />
                <Route path="/systems/:ein" element={<Protected><SystemDetail /></Protected>} />
                <Route path="/reports" element={<Protected><ReportList /></Protected>} />
                <Route path="/reports/new" element={<Protected><ReportNew /></Protected>} />
                <Route path="/reports/:id" element={<Protected><ReportDetail /></Protected>} />
                <Route path="/billing" element={<Protected><Billing /></Protected>} />
                <Route path="/admin/telemetry" element={<Protected><Telemetry /></Protected>} />
                <Route path="/admin/data-quality" element={<Protected><DataQuality /></Protected>} />
                <Route path="/admin/systems/add" element={<Protected><AdminAddSystem /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </PaywallProvider>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
